package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.addToExistingHintText
import com.milta.core.localization.addedFileAnalysingText
import com.milta.core.localization.addedFileFailedText
import com.milta.core.localization.addressRefusedMessage
import com.milta.feature.route.domain.entity.GeocodeStatus
import androidx.compose.runtime.collectAsState
import com.milta.feature.aicredit.presentation.AiCreditStore
import com.milta.feature.imports.presentation.documentErrorTexts
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import com.milta.feature.route.presentation.AddedFileFailure
import com.milta.feature.route.presentation.AddedFileFailureReason
import com.milta.feature.route.presentation.WebImportRouteIntent
import org.koin.compose.koinInject
import com.milta.feature.route.presentation.WebImportRouteRow
import com.milta.feature.route.presentation.WebRowEditor
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Retour 26/10 (demande 2) — pieces of the web "Ajouter une tournée" wizard: Material icons
 * (Outlined) instead of text characters, the "Ajouter des livraisons" menu (R9) and the edit /
 * manual-entry modal with the house-number check at "Enregistrer" (R4).
 */

/** Material Icons (Outlined) used by the wizard, as 24×24 SVG paths. */
internal enum class WebIconName(val path: String) {
    Add("M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"),
    Check("M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"),
    CheckCircle("M16.59 7.58L10 14.17l-3.59-3.58L5 12l5 5 8-8zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"),
    Close("M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"),
    Edit("M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM5.92 19H5v-.92l9.06-9.06.92.92L5.92 19zM20.71 5.63l-2.34-2.34c-.2-.2-.45-.29-.71-.29s-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41z"),
    ExpandMore("M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"),
    Place("M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 2.88-2.88 7.19-5 9.88C9.92 16.21 7 11.85 7 9zm5 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"),
    Refresh("M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"),
    Send("M4.01 6.03l7.51 3.22-7.52-1 .01-2.22m7.5 8.72L4 17.97v-2.22l7.51-1M2.01 3L2 10l15 2-15 2 .01 7L23 12 2.01 3z"),
    WarningAmber("M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z"),
    Upload("M18 15v3H6v-3H4v3c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-3h-2zM7 9l1.41 1.41L11 7.83V16h2V7.83l2.59 2.58L17 9l-5-5-5 5z"),
    Description("M8 16h8v2H8zm0-4h8v2H8zm6-10H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"),
    Delete("M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"),
}

/** One Material icon, colored by [color] (CSS color, `currentColor` follows the text). */
@Composable
internal fun WebIcon(icon: WebIconName, size: Int = 18, color: String = "currentColor") {
    key(icon, size) {
        Span(
            attrs = {
                style {
                    property("display", "inline-flex"); property("flex-shrink", "0")
                    property("width", "${size}px"); property("height", "${size}px")
                    property("color", color)
                }
                ref { element ->
                    element.innerHTML =
                        "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" width=\"$size\" height=\"$size\" " +
                        "fill=\"currentColor\" aria-hidden=\"true\"><path d=\"${icon.path}\"/></svg>"
                    onDispose { }
                }
            },
        )
    }
}

/** Overlay + card shared by the wizard's modals (same look as the existing confirmation dialogs). */
@Composable
internal fun WebModal(widthPx: Int = 460, onDismiss: () -> Unit, content: @Composable () -> Unit) {
    Div(
        attrs = {
            onClick { onDismiss() }
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
                property("background", "rgba(30,58,74,0.45)")
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("z-index", "1000")
            }
        },
    ) {
        Div(
            attrs = {
                onClick { it.stopPropagation() }
                style {
                    property("background", WebSurface); property("border-radius", "14px")
                    property("width", "${widthPx}px"); property("max-width", "calc(100vw - 32px)")
                    property("box-sizing", "border-box")
                    property("box-shadow", "0 12px 32px rgba(30,58,74,0.25)")
                    padding(22.px)
                    property("display", "flex"); property("flex-direction", "column"); property("gap", "8px")
                }
            },
        ) { content() }
    }
}

@Composable
private fun ModalTitle(text: String) {
    Div(attrs = { style { property("font-size", "16px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "4px") } }) {
        Text(text)
    }
}

@Composable
private fun ModalActions(content: @Composable () -> Unit) {
    Div(attrs = { style { property("display", "flex"); property("justify-content", "flex-end"); property("gap", "10px"); property("margin-top", "12px") } }) {
        content()
    }
}

@Composable
private fun ModalSecondaryButton(text: String, onClick: () -> Unit) {
    Button(
        attrs = {
            onClick { onClick() }
            style {
                property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                property("background", WebSurface); property("color", WebTextPrimary)
                property("font-size", "13px"); property("font-weight", "600")
                property("padding", "9px 16px"); property("cursor", "pointer")
            }
        },
    ) { Text(text) }
}

/** R9 — "Ajouter des livraisons": Importer un fichier (Excel, CSV, PDF) / Saisie manuelle. */
@Composable
internal fun AddDeliveriesMenuDialog(rowCount: Int, onIntent: (WebImportRouteIntent) -> Unit, onDismiss: () -> Unit) {
    val strings = LocalStrings.current
    val routeCreation = strings.routeCreation
    WebModal(onDismiss = onDismiss) {
        ModalTitle(strings.route.addDeliveriesAction)
        Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary); property("margin-bottom", "6px") } }) {
            Text(routeCreation.addToExistingHintText(rowCount))
        }
        FilePickerLabel(
            accept = ".xlsx,.xls,.csv,.pdf",
            onFilePicked = { bytes, name ->
                onDismiss()
                onIntent(WebImportRouteIntent.PickFile(bytes, name))
            },
        ) {
            MenuOption(WebIconName.Upload, routeCreation.importFileAction, routeCreation.importFileHint)
        }
        Div(attrs = {
            onClick {
                onDismiss()
                onIntent(WebImportRouteIntent.OpenRowEditor(null))
            }
        }) {
            MenuOption(WebIconName.Edit, routeCreation.manualEntryAction, routeCreation.manualEntryHint)
        }
        ModalActions { ModalSecondaryButton(strings.common.cancel, onDismiss) }
    }
}

@Composable
private fun MenuOption(icon: WebIconName, title: String, hint: String) {
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "12px")
                padding(12.px); property("border", "1px solid $WebBorder"); property("border-radius", "12px")
                property("cursor", "pointer"); property("text-align", "left")
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("width", "38px"); property("height", "38px"); property("border-radius", "11px")
                    property("background", WebAccentLight)
                    property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                }
            },
        ) { WebIcon(icon, size = 20, color = WebTealAccent) }
        Div {
            Div(attrs = { style { property("font-size", "14px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(title) }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(hint) }
        }
    }
}

/**
 * R4 — "Modifier la livraison" (pencil) / "Ajouter une livraison" (Saisie manuelle): one address
 * field (HERE autocomplete), checked at "Enregistrer"; refused → nothing saved, red field and the
 * motif with an example built on the street typed.
 */
@Composable
internal fun RowEditorDialog(
    editor: WebRowEditor,
    initial: WebImportRouteRow?,
    onIntent: (WebImportRouteIntent) -> Unit,
) {
    val strings = LocalStrings.current
    val columns = strings.webImportRoute
    var name by remember(editor.rowId) { mutableStateOf(initial?.recipientName.orEmpty()) }
    var address by remember(editor.rowId) { mutableStateOf(initial?.address.orEmpty()) }
    var phone by remember(editor.rowId) { mutableStateOf(initial?.phoneNumber.orEmpty()) }
    var deadline by remember(editor.rowId) { mutableStateOf(initial?.deadlineTime) }
    var packages by remember(editor.rowId) { mutableStateOf(initial?.packageCount?.coerceAtLeast(1) ?: 1) }
    // The refusal concerns the address that was checked: typing again clears the red state.
    val refusal = editor.refusal?.takeIf { address.trim() == editor.refusedAddress }
    val close = { onIntent(WebImportRouteIntent.CloseRowEditor) }
    WebModal(onDismiss = close) {
        ModalTitle(if (editor.rowId == null) strings.routeCreation.addDeliveryTitle else strings.routeCreation.editDeliveryTitle)
        FieldLabel(columns.columnHeaderRecipientName)
        TextInput(name) { name = it }
        FieldLabel(columns.columnHeaderAddress)
        AddressAutocompleteInput(
            value = address,
            onChange = { address = it },
            onSuggestionPicked = { address = it.label },
            invalid = refusal != null,
        )
        if (refusal != null) {
            Div(attrs = { style { property("display", "flex"); property("gap", "6px"); property("align-items", "flex-start"); property("color", WebErrorInk); property("font-size", "12px"); property("font-weight", "600") } }) {
                WebIcon(WebIconName.WarningAmber, size = 15, color = WebErrorInk)
                Text(strings.routeCreation.addressRefusedMessage(refusal, editor.refusedAddress))
            }
        }
        FieldLabel(columns.columnHeaderPhone)
        TextInput(phone) { phone = it }
        Div(attrs = { style { property("display", "flex"); property("gap", "18px"); property("align-items", "flex-end") } }) {
            Div {
                FieldLabel(columns.columnHeaderDeadline)
                TimeField(deadline) { deadline = it }
            }
            Div {
                FieldLabel(columns.columnHeaderPackageCount)
                PackageCountStepper(packages) { packages = it }
            }
        }
        ModalActions {
            ModalSecondaryButton(strings.common.cancel, close)
            val canSave = address.isNotBlank() && !editor.isSaving
            Button(
                attrs = {
                    if (canSave) {
                        onClick {
                            onIntent(
                                WebImportRouteIntent.SaveRowEditor(
                                    (initial ?: WebImportRouteRow(id = -1, recipientName = null, address = null, phoneNumber = null, deadlineTime = null, hasMissingFields = false, packageCount = 1))
                                        .copy(
                                            recipientName = name.trim().ifBlank { null },
                                            address = address.trim(),
                                            phoneNumber = phone.trim().ifBlank { null },
                                            deadlineTime = deadline,
                                            packageCount = packages,
                                            geocodeStatus = initial?.geocodeStatus ?: GeocodeStatus.Pending,
                                        ),
                                ),
                            )
                        }
                    }
                    style {
                        gradientButtonStyle(this)
                        if (!canSave) {
                            property("opacity", "0.5"); property("cursor", "not-allowed"); property("box-shadow", "none")
                        }
                    }
                },
            ) {
                if (editor.isSaving) {
                    WebSpinner(size = 14, strokeWidth = 2, color = "#FFFFFF", trackColor = "rgba(255,255,255,0.35)")
                } else {
                    Text(strings.common.save)
                }
            }
        }
    }
}

@Composable
private fun FieldLabel(text: String) {
    Div(attrs = { style { property("font-size", "12px"); property("font-weight", "700"); property("color", WebTextSecondary); property("margin-top", "4px") } }) {
        Text(text)
    }
}

@Composable
private fun TextInput(value: String, onChange: (String) -> Unit) {
    Input(
        type = InputType.Text,
        attrs = {
            value(value)
            onInput { onChange(it.value) }
            style {
                width(100.percent); property("box-sizing", "border-box")
                property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                property("padding", "9px 10px"); property("font-size", "13.5px")
            }
        },
    )
}

/** R9 — an added file being analysed, or that could not be imported, shown above the table. */
@Composable
internal fun AddedFileBanner(analysingFileName: String?, failure: AddedFileFailure?, onDismissFailure: () -> Unit) {
    val strings = LocalStrings.current.routeCreation
    val webStrings = LocalStrings.current.webImportRoute
    val ai = LocalStrings.current.aiCredit
    val aiCredit by koinInject<AiCreditStore>().credit.collectAsState()
    val closeLabel = LocalStrings.current.route.proofPhotoViewerCloseLabel
    if (analysingFileName != null) {
        Div(
            attrs = {
                style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                    property("border-radius", "10px"); padding(11.px, 14.px); property("margin-bottom", "14px")
                    property("background", WebActiveTint); property("color", WebPrimary); property("font-size", "13px"); property("font-weight", "600")
                }
            },
        ) {
            WebSpinner(size = 14, strokeWidth = 2)
            Text(strings.addedFileAnalysingText(analysingFileName))
        }
    }
    if (failure != null) {
        // The texts of the same failure on the first import (Écran 1); the generic one only when unknown.
        val name = failure.fileName
        val (title, message) = when (val reason = failure.reason) {
            is AddedFileFailureReason.AiCreditExhausted -> {
                val date = OptimizationQuotaFormat.dayMonth(reason.resetAt).ifEmpty { OptimizationQuotaFormat.dayMonth(aiCredit?.resetAt) }
                (if (reason.isSpreadsheet) ai.spreadsheetExhaustedTitle else ai.pdfRefusedTitle) to
                    (if (reason.isSpreadsheet) ai.spreadsheetExhaustedWebMessage else ai.pdfRefusedMessage)
                        .fillPlaceholders("file" to name, "date" to date)
            }
            AddedFileFailureReason.TooManyStops -> webStrings.tooManyStopsError to ""
            is AddedFileFailureReason.Document -> LocalStrings.current.imports.documentErrorTexts(reason.kind).let { it.first to it.second }
            AddedFileFailureReason.ServiceUnavailable -> webStrings.serviceUnavailableTitle to webStrings.serviceUnavailableMessage
            AddedFileFailureReason.Unknown -> strings.addedFileFailedText(name) to ""
        }
        Div(
            attrs = {
                style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                    property("border-radius", "10px"); padding(11.px, 14.px); property("margin-bottom", "14px")
                    property("background", WebErrorBg); property("color", WebErrorInk); property("font-size", "13px")
                }
            },
        ) {
            WebIcon(WebIconName.WarningAmber, size = 16, color = WebErrorInk)
            Div(attrs = { style { property("flex", "1") } }) {
                Div(attrs = { style { property("font-weight", "700") } }) { Text(title) }
                if (message.isNotEmpty()) Div(attrs = { style { property("margin-top", "2px") } }) { Text(message) }
            }
            Button(
                attrs = {
                    attr("aria-label", closeLabel)
                    onClick { onDismissFailure() }
                    style {
                        property("border", "none"); property("background", "transparent"); property("cursor", "pointer")
                        property("color", WebErrorInk); property("display", "flex"); padding(2.px)
                    }
                },
            ) { WebIcon(WebIconName.Close, size = 16) }
        }
    }
}
